import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { isPlainObject } from 'lodash'
import QueryString from 'qs'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { apiClient } from '../client'
import { PageListParams } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'

export const createDiaryFunctions = <
    TEntity,
    TUpdateRequest extends { id: number | string },
    TListResponse,
    TDetailResponse,
    TDeleteResponse,
    TCriteria = unknown,
>(
    basePath: string
) => {
    const getDiaries = async (payload: PageListParams<TCriteria>): Promise<TListResponse> => {
        const response = await apiClient(basePath, {
            params: payload,
            paramsSerializer: (params: PageListParams<TCriteria>) => {
                const criteria = isPlainObject(params.criteria) ? params.criteria : {}
                return QueryString.stringify({ ...params, criteria })
            },
        })
        return response.data
    }

    const getDiary = async (id: number | string): Promise<TDetailResponse> => {
        const response = await apiClient(`${basePath}/${id}`)
        return response.data
    }

    const updateDiary = async (request: TUpdateRequest): Promise<TEntity> => {
        const response = await apiClient(`${basePath}/${request.id}`, {
            method: 'PUT',
            data: request,
        })
        return response.data
    }

    const deleteDiary = async (id: number | string): Promise<TDeleteResponse> => {
        const response = await apiClient(`${basePath}/${id}`, {
            method: 'DELETE',
        })
        return response.data
    }

    const downloadDiaryPdf = async (id: number | string): Promise<string> => {
        const response = await apiClient(`${basePath}/download/${id}`)
        return response.data
    }

    return { getDiaries, getDiary, updateDiary, deleteDiary, downloadDiaryPdf }
}

interface DiaryFunctions<TEntity, TUpdateRequest, TListResponse, TDetailResponse, TDeleteResponse, TCriteria = unknown> {
    getDiaries: (payload: PageListParams<TCriteria>) => Promise<TListResponse>
    getDiary: (id: number | string) => Promise<TDetailResponse>
    updateDiary: (request: TUpdateRequest) => Promise<TEntity>
    deleteDiary: (id: number | string) => Promise<TDeleteResponse>
    downloadDiaryPdf: (id: number | string) => Promise<string>
}

export const createDiaryHooks = <
    TEntity,
    TUpdateRequest extends { id: number | string },
    TListResponse,
    TDetailResponse,
    TDeleteResponse,
    TCriteria = unknown,
>(
    namespace: string,
    diaryFunctions: DiaryFunctions<TEntity, TUpdateRequest, TListResponse, TDetailResponse, TDeleteResponse, TCriteria>
) => {
    const { getDiaries, getDiary, updateDiary, deleteDiary, downloadDiaryPdf } = diaryFunctions

    return {
        GetDiaries: {
            useQuery: (params: PageListParams<TCriteria>, options?: Omit<UseQueryOptions<TListResponse, AxiosError>, 'queryKey' | 'queryFn'>) =>
                useQuery({
                    queryKey: [namespace, 'list', params],
                    queryFn: () => getDiaries(params),
                    staleTime: 1 * 60 * 1000,
                    ...options,
                }),
        },
        GetDiary: {
            useQuery: (id: number | string, options?: Omit<UseQueryOptions<TDetailResponse, AxiosError>, 'queryKey' | 'queryFn'>) =>
                useQuery({
                    queryKey: [namespace, String(id)],
                    queryFn: () => getDiary(id),
                    enabled: !!id,
                    staleTime: 1 * 60 * 1000,
                    ...options,
                }),
        },
        UpdateDiary: {
            useMutation: (options?: UseMutationOptions<TEntity, AxiosError, TUpdateRequest>) =>
                useMutation({
                    onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                    mutationFn: (request: TUpdateRequest) => updateDiary(request),
                    onSuccess: (_data, variables, context) => {
                        const toastId = context as number
                        toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                        invalidateQueries([namespace, 'list'])
                        invalidateQueries([namespace, String(variables.id)])
                    },
                    onError: (_error, _variables, context) => {
                        const toastId = context as number
                        toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                    },
                    ...options,
                }),
        },
        DeleteDiary: {
            useMutation: (options?: UseMutationOptions<TDeleteResponse, AxiosError, number | string>) =>
                useMutation({
                    onMutate: (id: number | string) => toast.loading(i18n.t('toast.loading.delete') as string, { toastId: id }),
                    mutationFn: (id: number | string) => deleteDiary(id),
                    onSuccess: (_data, id) => {
                        updateToast(id, i18n.t('toast.update.delete.success') as string, 'success')
                        invalidateQueries([namespace, 'list'])
                    },
                    onError: (_error, id) => {
                        updateToast(id, i18n.t('toast.update.delete.error') as string, 'error')
                    },
                    ...options,
                }),
        },
        DownloadDiaryPdf: {
            useMutation: (options?: UseMutationOptions<string, AxiosError, number | string>) =>
                useMutation({
                    mutationFn: (id: number | string) => downloadDiaryPdf(id),
                    ...options,
                }),
        },
    }
}
